<template>
    <PreEmptiveRightsTable>
        <template #actions>
            <CreateButton
                v-if="userStore.can('update_asset')"
                @click.stop="handleCreatePreEmptiveRights"
            />
        </template>
    </PreEmptiveRightsTable>

    <CreateOrEditPreEmptiveRightsModal
        v-if="createOrEditPreEmptiveRightsModalOpen"
        v-model:open="createOrEditPreEmptiveRightsModalOpen"
        v-model:preselected="preselected"
        :model-value="preEmptiveRightsToCreateOrEdit"
        @submit="handleSubmitCreateOrEditPreEmptiveRights"
    />
</template>

<script setup lang="ts">
import { useTable } from '@src/composables/common'
import { Account, TableSorting } from '@src/types'
import { useUserStore } from '@src/utils/stores/user'
import { useToastStore } from '@src/utils/stores/toast'
import { computed, h, onMounted, ref, toRefs, VNode } from 'vue'
import {
    getPreEmptiveRights,
    PreEmptiveRight,
    updatePreEmptiveRights,
} from '@utils/api/secondaryMarket'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBuildingColumns, faUser } from '@fortawesome/free-solid-svg-icons'
import CopyButton from '@partials/actions/CopyButton.vue'
import { useI18n } from 'vue-i18n'
import CreateButton from '@partials/actions/CreateButton.vue'
import CreateOrEditPreEmptiveRightsModal from '@partials/secondaryMarket/CreateOrEditPreEmptiveRightsModal.vue'
import { formatSecondaryMarketValue } from '@partials/secondaryMarket/formatSecondaryMarketValue'

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            asset_uid?: string | null
            search?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)

const i18n = useI18n()
const userStore = useUserStore()
const toast = useToastStore()

const { initialFilters } = toRefs(props)

const preEmptiveRights = ref<PreEmptiveRight[]>([])
const preEmptiveRightsCount = ref<number>(0)
const createOrEditPreEmptiveRightsModalOpen = ref(false)
const preEmptiveRightsToCreateOrEdit = ref<PreEmptiveRight | null>(null)
const preselected = ref(false)

const {
    TableWrapper: PreEmptiveRightsTable,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    setFilters,
    reloadTable,
} = useTable({
    loadData: loadPreEmptiveRights,
    filters: {
        asset_uid: initialFilters.value?.asset_uid || null,
        search: initialFilters.value?.search || null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'pre-emptive-rights-table',
        label: 'pre_emptive_rights_table',
        help: 'help_pre_emptive_rights_table',
        pagination: true,
        data: computed(() => preEmptiveRights.value),
        total: computed(() => preEmptiveRightsCount.value),
        sorting: ref<TableSorting[]>([
            {
                field: 'account_title',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'account_type',
                key: 'account_type',
                sorting: true,
                center: true,
                cellRenderer: (props: { rowData: Account }) => {
                    if (props.rowData.account_type === 'legal') {
                        return h(FontAwesomeIcon, {
                            icon: faBuildingColumns,
                            class: 'text-xl',
                            title: i18n.t('legal_account'),
                        })
                    } else {
                        return h(FontAwesomeIcon, {
                            icon: faUser,
                            class: 'text-xl',
                            title: i18n.t('natural_account'),
                        })
                    }
                },
            },
            {
                label: 'id',
                key: 'uid',
                sorting: false,
                condition: userStore.can('see_id_columns'),
                cellRenderer: (props: { rowData: Account }): VNode | null => {
                    let id: string | null = ''
                    if (props.rowData.account_type === 'legal') {
                        id = props.rowData.legal_profile_uid
                    } else if (props.rowData.account_type === 'natural') {
                        id = props.rowData.natural_profile_uid
                    }

                    const url = accountTableUrl(props.rowData)

                    return id
                        ? h(
                              'div',
                              {
                                  class: 'flex items-center',
                              },
                              [
                                  h(
                                      'a',
                                      {
                                          href: url,
                                          class: 'text-blue-500',
                                      },
                                      id
                                  ),
                                  h(CopyButton, {
                                      value: id,
                                  }),
                              ]
                          )
                        : null
                },
            },
            {
                label: 'account_title',
                key: 'account_title',
                url: accountTableUrl,
                sorting: true,
                copy: true,
            },
            {
                label: 'email',
                key: 'email',
                sorting: true,
                copy: true,
            },
            {
                label: 'quantity',
                key: 'quantity',
                sorting: true,
                cellRenderer: (props: { rowData: PreEmptiveRight }) => {
                    return formatSecondaryMarketValue(props.rowData.quantity)
                },
            },
        ],
        actions: [
            {
                action: (rowData: PreEmptiveRight) => {
                    createOrEditPreEmptiveRightsModalOpen.value = true
                    preEmptiveRightsToCreateOrEdit.value = {
                        ...rowData,
                    } as PreEmptiveRight
                    preselected.value = true
                },
                icon: 'fa-solid fa-pen',
                title: 'edit',
                condition: userStore.can('update_account'),
            },
        ],
    },
})

async function loadPreEmptiveRights() {
    try {
        const response = await getPreEmptiveRights(
            filters.value,
            page.value,
            limit.value,
            sorting.value
        )

        const rawData = response?.data || response
        const items = rawData?.data || (Array.isArray(rawData) ? rawData : [])

        // Resolve total records count correctly from Laravel Paginator structure
        const total =
            rawData?.total ??
            rawData?.meta?.total ??
            rawData?.count ??
            items.length

        preEmptiveRights.value = items
        preEmptiveRightsCount.value = Number(total)

        return {
            data: items,
            total: Number(total),
        }
    } catch (error) {
        console.error('Error loading pre-emptive rights:', error)
        preEmptiveRights.value = []
        preEmptiveRightsCount.value = 0
        return {
            data: [],
            total: 0,
        }
    }
}

function accountTableUrl(account: Account) {
    if (account.account_type === 'legal') {
        return '/legalProfile/' + account.legal_profile_uid
    } else if (account.account_type === 'natural') {
        return '/naturalProfile/' + account.natural_profile_uid
    }

    return ''
}

function handleCreatePreEmptiveRights(): void {
    createOrEditPreEmptiveRightsModalOpen.value = true
    preEmptiveRightsToCreateOrEdit.value = {
        account_uid: null,
        quantity: 0,
        asset_uid: initialFilters.value?.asset_uid || null,
    } as any
    preselected.value = false
}

async function handleSubmitCreateOrEditPreEmptiveRights(
    payload?: PreEmptiveRight
) {
    const dataToSend = payload || preEmptiveRightsToCreateOrEdit.value
    if (!dataToSend) return

    try {
        await updatePreEmptiveRights(dataToSend)
        toast.success(i18n.t('toast_success_update_pre_emptive_rights'))
        createOrEditPreEmptiveRightsModalOpen.value = false

        if (reloadTable) {
            await reloadTable()
        } else {
            await loadPreEmptiveRights()
        }
    } catch (error) {
        toast.error(i18n.t('toast_error_update_pre_emptive_rights'))
        console.error(error)
    }
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
    }

    await loadPreEmptiveRights()
})
</script>
